import { noop } from '@reatom/framework'
import { useAtom } from '@reatom/npm-react'
import styled, { css } from 'styled-components'
import { match } from 'ts-pattern'

import { delugeIdAtom } from '@root/entities/auth'
import { WalletsTable } from '@root/features/portfolio/wallets-table/ui/wallets-table'
import { UserProfileDeployedTokensTable } from '@root/features/profile/deployed-tokens/ui/user-profile-deployed-tokens-table'
import { UserProfileHoldingsTable } from '@root/features/profile/holdings/ui/user-profile-holdings-table'
import { useTranslation, type TransKey } from '@root/shared/localization'
import { mediaMaxMobile, UnderlineTabs } from '@root/shared/ui-kit'

import { statisticsTabAtom } from '../atoms/statistics-tab-atom'
import { StatisticsTab, StatisticsTabs } from '../model/statistics-tabs'

import { StatisticsTablesSkeleton } from './profile-board-statistics-tables-skeleton'
import { ProfileTradesTable } from './profile-trades-table'

import type { ProfileWallet } from '@root/entities/profile/model'
import type { DelugeId } from '@root/entities/user-short'

const TabTranslationMap: Record<StatisticsTab, TransKey> = {
  [StatisticsTab.Trades]: 'profile.statistics.tabs.trades',
  [StatisticsTab.Holdings]: 'profile.statistics.tabs.holdings',
  [StatisticsTab.DeployedTokens]: 'profile.statistics.tabs.deployed-tokens',
}

export const StatisticsTables = ({
  delugeId,
  className,
  wallets,
}: {
  delugeId: DelugeId | undefined
  wallets: ReadonlyArray<ProfileWallet.ProfileWallet>
  className?: string
}) => {
  const { t } = useTranslation()
  const [statisticsTab, setStatisticsTab] = useAtom(statisticsTabAtom)
  const [currentDelugeId] = useAtom(delugeIdAtom)

  const renderTable = () => {
    if (!delugeId) {
      return <SkeletonContainer columnsCount={7} />
    }

    return match(statisticsTab)
      .with(StatisticsTab.Trades, () => (
        <ProfileTradesTable wallets={wallets} />
      ))
      .with(StatisticsTab.Holdings, () =>
        currentDelugeId === delugeId ? (
          <WalletsTable
            onEditClick={noop}
            onTransactionsClick={noop}
            selectedWalletIds={new Set()}
            onToggleSelection={noop}
            onSelectSingleWallet={noop}
            withSelection={false}
            withTransactions={false}
          />
        ) : (
          <UserProfileHoldingsTable delugeId={delugeId} />
        ),
      )
      .with(StatisticsTab.DeployedTokens, () => (
        <UserProfileDeployedTokensTable delugeId={delugeId} />
      ))
      .exhaustive()
  }

  return (
    <Root className={className}>
      <Tabs value={statisticsTab} onChange={setStatisticsTab}>
        {StatisticsTabs.map((tab) => {
          const isDev = import.meta.env.DEV

          if (!isDev && tab === StatisticsTab.Trades) {
            return undefined
          }
          return (
            <UnderlineTabs.Tab key={tab} value={tab}>
              <Label>{t(TabTranslationMap[tab])}</Label>
            </UnderlineTabs.Tab>
          )
        })}
      </Tabs>
      <TableContainer>{renderTable()}</TableContainer>
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  margin-top: 12px;
  border-radius: 12px;
  border: 1px solid var(--background-3);
  background: var(--background-1);

  ${mediaMaxMobile(css`
    border-radius: 0;
    margin-top: 6px;
  `)}
`
const Label = styled.span`
  white-space: nowrap;
`
const TableContainer = styled.div`
  height: 70vh;
  overflow: hidden;
`
const SkeletonContainer = styled(StatisticsTablesSkeleton)`
  height: var(--viewport-height);
`

const Tabs = styled(UnderlineTabs<StatisticsTab>)`
  --tab-padding-inline: 12px;

  width: 100%;
  padding-inline: 8px;
  text-transform: capitalize;
  display: flex;
`
